import type { MapAdapter, MapInitOptions, MarkerSetupOptions, LatLng } from './MapAdapter';

declare const naver: any;

export class NaverMapAdapter implements MapAdapter {
    private map: any = null;
    private markers: Record<string, any> = {};
    private infoWindows: Record<string, any> = {};
    private pinOptions: Record<string, { url: string; w: number; h: number }> = {};
    private markerLatLng: Record<string, any> = {};
    private ripples: Record<string, any> = {};

    async init(options: MapInitOptions): Promise<void> {
        const { container, center, zoom } = options;
        this.map = new naver.maps.Map(container, {
            center: new naver.maps.LatLng(center.lat, center.lng),
            zoom,
            mapTypeControl: false,
            scaleControl: false,
            logoControl: false,
            mapDataControl: false,
        });
    }

    addMarker(id: string, options: MarkerSetupOptions): void {
        const { position, title, pin } = options;
        const latlng = new naver.maps.LatLng(position.lat, position.lng);
        this.markerLatLng[id] = latlng;
        const markerOptions: any = { position: latlng, map: this.map, title };
        if (pin) {
            this.pinOptions[id] = { url: pin.url, w: pin.width, h: pin.height };
            markerOptions.icon = this.buildIcon(pin.url, pin.width, pin.height, false);
        }
        this.markers[id] = new naver.maps.Marker(markerOptions);
    }

    setMarkerPosition(id: string, position: LatLng): void {
        const latlng = new naver.maps.LatLng(position.lat, position.lng);
        this.markerLatLng[id] = latlng;
        if (this.markers[id]) {
            this.markers[id].setPosition(latlng);
        }
        if (this.ripples[id]) {
            this.ripples[id].setMap(null);
            delete this.ripples[id];
        }
    }

    private buildIcon(url: string, w: number, h: number, active: boolean): any {
        const className = ['store-map-pin', this.pinSizeClass(w, h), active ? 'store-map-pin--active' : '']
            .filter(Boolean)
            .join(' ');
        return {
            content: `<span class="${className}"><img class="store-map-pin__image" src="${this.escapeAttribute(url)}" alt=""></span>`,
            size: new naver.maps.Size(w, h),
            anchor: new naver.maps.Point(w / 2, h),
        };
    }

    private pinSizeClass(w: number, h: number): string {
        if (w === 112 && h === 140) return 'store-map-pin--public';
        if (w === 40 && h === 40) return 'store-map-pin--preview';
        return '';
    }

    private escapeAttribute(value: string): string {
        return value
            .replace(/&/g, '&amp;')
            .replace(/"/g, '&quot;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
    }

    setMarkerActive(id: string, active: boolean): void {
        const pin = this.pinOptions[id];
        if (!pin || !this.markers[id]) return;
        this.markers[id].setIcon(this.buildIcon(pin.url, pin.w, pin.h, active));
        this.markers[id].setZIndex(active ? 10 : 1);
    }

    setInfoWindowContent(id: string, html: string): void {
        this.infoWindows[id] = new naver.maps.InfoWindow({
            content: html,
            borderWidth: 0,
            disableAnchor: false,
            backgroundColor: 'transparent',
            pixelOffset: new naver.maps.Point(0, -8),
        });
    }

    openInfoWindow(id: string): void {
        if (this.infoWindows[id] && this.markers[id]) {
            this.infoWindows[id].open(this.map, this.markers[id]);
        }
    }

    closeAllInfoWindows(): void {
        Object.values(this.infoWindows).forEach((w) => w.close());
    }

    setMarkerVisible(id: string, visible: boolean): void {
        if (this.markers[id]) this.markers[id].setVisible(visible);
    }

    setRipple(id: string, show: boolean): void {
        if (!show) {
            if (this.ripples[id]) {
                this.ripples[id].setMap(null);
                delete this.ripples[id];
            }
            return;
        }
        if (this.ripples[id]) return;
        const pos = this.markerLatLng[id];
        if (!pos || !this.map) return;

        const el = document.createElement('div');
        el.className = 'store-pin-ripple';

        // Prototype-chain based OverlayView (Naver Maps pattern)
        function NaverRipple(this: any) {
            this._el = el;
            this._pos = pos;
        }
        NaverRipple.prototype = new naver.maps.OverlayView();
        NaverRipple.prototype.onAdd = function (this: any) {
            this.getPanes().overlayLayer.appendChild(this._el);
        };
        NaverRipple.prototype.draw = function (this: any) {
            const proj = this.getProjection();
            const pt = proj.fromCoordToOffset(this._pos);
            this._el.style.left = pt.x - 18 + 'px';
            this._el.style.top = pt.y - 18 + 'px';
        };
        NaverRipple.prototype.onRemove = function (this: any) {
            if (this._el.parentNode) this._el.parentNode.removeChild(this._el);
        };

        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        const overlay = new (NaverRipple as any)();
        overlay.setMap(this.map);
        this.ripples[id] = overlay;
    }

    onMarkerClick(id: string, callback: () => void): void {
        if (this.markers[id]) {
            naver.maps.Event.addListener(this.markers[id], 'click', callback);
        }
    }

    panTo(pos: LatLng): void {
        if (this.map) this.map.panTo(new naver.maps.LatLng(pos.lat, pos.lng));
    }

    setZoom(zoom: number): void {
        if (this.map) this.map.setZoom(zoom);
    }

    getZoom(): number {
        return this.map ? (this.map.getZoom() as number) : 0;
    }

    fitBounds(positions: LatLng[], padding = 60): void {
        if (!this.map || positions.length === 0) return;
        if (positions.length === 1) {
            this.panTo(positions[0]);
            this.setZoom(15);
            return;
        }
        const bounds = new naver.maps.LatLngBounds();
        positions.forEach((p) => bounds.extend(new naver.maps.LatLng(p.lat, p.lng)));
        this.map.fitBounds(bounds, { top: padding, right: padding, bottom: padding, left: padding });
    }
}
